<div
  class="data-table-container surface-default rounded-2xl border border-gray-4 bg-white dark:bg-gray-1 shadow-sm flex flex-col"
>
  <!-- Table Header Actions (search, filters, customized columns) -->
  <div class="px-5 py-3 border-b border-gray-4 flex items-center min-h-[56px]">
    <aura-data-header-actions
      class="w-full"
      [searchPlaceHolder]="searchPlaceHolder()"
      [pagination]="pagination()"
      [topButtons]="topButtons()"
      [loading]="loading()"
      [topMenu]="topMenu()"
      [hideSearch]="hideSearch()"
      [selectedCount]="selectedCount()"
      [filters]="filters()"
      [filterTemplate]="filterTemplate"
      [mappedFilters]="mappedFilters()"
      [columns]="columns()"
      (columnsChange)="columns.set($event)"
      (columnChanged)="columnChanged($event)"
      (clearSelection)="clearSelection()"
      (removeFilter)="removeFilter.emit($event)"
      (filterView)="onFilterViewChanged($event)"
      (clearFiltering)="clearFiltering.emit($event)"
      (triggerFiltering)="triggerFiltering.emit($event)"
      (triggerRefresh)="triggerRefresh.emit($event)"
      (searchChanged)="onSearchChanged($event)"
    ></aura-data-header-actions>
  </div>

  <!-- Table Body / Skeleton Area -->
  <div class="data-list-table">
    <div class="relative overflow-x-auto min-h-[420px]">
      @if (loading()) {
        <div class="absolute inset-0 bg-white dark:bg-gray-1 z-10 overflow-hidden">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="mat-mdc-header-row border-b border-gray-4">
                @for (col of columns(); track col.key) {
                  @if (col.display !== false) {
                    <th class="py-3.5 px-4 text-xs font-bold uppercase text-indigo-11">
                      {{ col.label || '' }}
                    </th>
                  }
                }
              </tr>
            </thead>
            <tbody>
              @for (row of skeletonRows(); track row) {
                <tr
                  class="mat-mdc-row border-b border-gray-3 hover:bg-transparent"
                  [class.bg-gray-a2]="row % 2 === 1"
                >
                  @for (col of columns(); track col.key; let colIndex = $index) {
                    @if (col.display !== false) {
                      <td class="py-4.5 px-4">
                        @if (col.key === 'checkbox') {
                          <ngx-skeleton-loader
                            [theme]="{ width: '18px', height: '18px', 'border-radius': '4px', margin: '0' }"
                            animation="pulse"
                          />
                        } @else if (col.key === 'actions') {
                          <div class="flex justify-end">
                            <ngx-skeleton-loader
                              [theme]="{ width: '28px', height: '28px', 'border-radius': '50%', margin: '0' }"
                              animation="pulse"
                            />
                          </div>
                        } @else {
                          <ngx-skeleton-loader
                            [theme]="{
                              width: getSkeletonWidth(row, colIndex),
                              height: '18px',
                              'border-radius': '6px',
                              margin: '0',
                            }"
                            animation="pulse"
                          />
                        }
                      </td>
                    }
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <table [dataSource]="localItems()" [multiTemplateDataRows]="expandable()" mat-table>
        <tr *matHeaderRowDef="columnKeys()" mat-header-row></tr>
        <tr
          *matRowDef="let row; columns: columnKeys()"
          (click)="rowClickable() ? onItemClicked(row) : null"
          (auxclick)="rowClickable() ? onItemAuxClicked(row) : null"
          mat-row
          class="group"
          [class.row-clickable]="rowClickable() && !disableItemClick()"
        ></tr>
        <ng-content select="[slot=columns]"></ng-content>

        @if (displayCheckbox()) {
          <ng-container matColumnDef="checkbox">
            <th *matHeaderCellDef mat-header-cell class="w-0 pr-0 pl-4">
              <mat-checkbox
                [(ngModel)]="allRowsSelected"
                (ngModelChange)="selectAllRows()"
                class="no-padding-checkbox top-1"
              ></mat-checkbox>
            </th>
            <td *matCellDef="let item" mat-cell class="w-0 pr-0 pl-4">
              <mat-checkbox
                [(ngModel)]="item.selected"
                (ngModelChange)="onRowSelect(item)"
                class="no-padding-checkbox top-1"
              ></mat-checkbox>
            </td>
          </ng-container>
        }

        @if (appStore.debugMode()) {
          <ng-container matColumnDef="debug">
            <th *matHeaderCellDef mat-header-cell>#</th>
            <td *matCellDef="let item" mat-cell>
              <aura-clipboard [textToCopy]="item._id">{{ item._id }}</aura-clipboard>
            </td>
          </ng-container>
        }

        @if (expandable()) {
          <tr *matRowDef="let row; columns: ['expandedDetail']" mat-row class="expandable-detail-row"></tr>
        }

        <tr *matNoDataRow class="mat-mdc-row empty-data">
          <td [attr.colspan]="columnKeys().length" class="mat-mdc-cell">
            <div class="flex flex-col items-center justify-center py-10 text-gray-10">
              <mat-icon class="text-gray-8 mb-2 scale-125">search_off</mat-icon>
              <span>{{ noDataLabel() }}</span>
            </div>
          </td>
        </tr>

        <ng-container matColumnDef="actions">
          <th *matHeaderCellDef mat-header-cell></th>
          <td *matCellDef="let item" mat-cell class="w-0">
            @if (itemActions().length > 1) {
              <button
                [matMenuTriggerFor]="menu"
                [matMenuTriggerData]="{ item: item }"
                (click)="$event.stopPropagation()"
                (menuOpened)="onMenuOpened(item)"
                mat-button
                class="py-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 cursor-pointer"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            } @else {
              <ng-container *ngTemplateOutlet="menuItemTemplate; context: { action: itemActions()[0], item }">
              </ng-container>
            }
          </td>
        </ng-container>
      </table>
    </div>

    <!-- Table Footer / Pagination -->
    @if (!hidePagination()) {
      <div
        class="table-footer flex flex-col sm:flex-row items-center justify-between px-5 py-1 border-t border-gray-4 bg-gray-a2 min-h-[52px]"
      >
        <div class="table-total-count my-2 sm:my-0">
          <span>Total :</span>
          <span class="total-number">{{ count() }}</span>
          <span>élément(s)</span>
        </div>
        <mat-paginator
          [length]="count()"
          [pageSize]="pagination().pageSize"
          [pageSizeOptions]="[5, 10, 25, 50]"
          [pageIndex]="pagination().page"
          (page)="onPageChanged($event)"
          class="rounded-xl bg-transparent!"
          showFirstLastButtons
          aria-label="Sélectionner une page"
        >
        </mat-paginator>
      </div>
    }
  </div>

  <mat-menu #menu="matMenu" overlapTrigger="true">
    <ng-template matMenuContent let-item="item">
      @for (action of itemActions(); track $index) {
        <button
          [class]="action.color"
          [disabled]="action.onDisabled ? action.onDisabled(item) : action.disabled"
          (click)="action.onClick(item)"
          mat-menu-item
        >
          <mat-icon [color]="action.color">{{ action.icon }}</mat-icon>
          <span>{{ action.label }}</span>
        </button>
      }
    </ng-template>
  </mat-menu>

  <ng-template #menuItemTemplate let-action="action" let-item="item">
    <button
      [disabled]="action.disabled"
      [matTooltip]="action.label"
      (click)="$event.stopPropagation(); action.onClick(item, $event)"
      mat-menu-item
      class="p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 cursor-pointer"
    >
      <mat-icon [color]="action.color" class="mr-0">{{ action.icon }}</mat-icon>
    </button>
  </ng-template>
</div>
